import { useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { toast } from "sonner";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Tablet dimensions (standard Android tablet 16:10 aspect ratio)
const TABLET_WIDTH = 1280;
const TABLET_HEIGHT = 800;

// Booma Logo
const BOOMA_LOGO_INVERTED = "https://customer-assets.emergentagent.com/job_510d4ad4-6c30-4bd6-a7e0-e51838ecf5ce/artifacts/vmrblhbh_booma_logo-3-invert.jpg";

// Static demo ads - 4 businesses with clickable deals
const DEMO_ADS = [
  {
    id: "demo-1",
    business_name: "St Kilda Beach Bar",
    tier: "GOLD",
    offer_text: "Happy Hour Special",
    deal_description: "2-for-1 cocktails every day 4-6pm",
    cta_text: "Claim Deal",
    deal_available: true,
    image_url: "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/f4e9827988e3118bcd7f1c916edb259b412beb6b733a8ee72d363bb92f1d27d7.png",
    distance: "450m"
  },
  {
    id: "demo-2",
    business_name: "Sydney Coffee Co",
    tier: "GOLD",
    offer_text: "FREE COFFEE",
    deal_description: "Free flat white with any breakfast order",
    cta_text: "Get Offer",
    deal_available: true,
    image_url: "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/6f3bc4553eac19a3c09c1e6762cfca43ca7c74aaaf54ecd788be4861e2eb12a7.png",
    distance: "1.2km"
  },
  {
    id: "demo-3",
    business_name: "Melbourne Pizza House",
    tier: "SILVER",
    offer_text: "20% OFF",
    deal_description: "20% off all large pizzas - dine in only",
    cta_text: "Save Now",
    deal_available: true,
    image_url: "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/ae707c420b32a559dfc5ae01a13136a3338cceea9a95f493082d868924c2ac5b.png",
    distance: "800m"
  },
  {
    id: "demo-4",
    business_name: "Brisbane Fitness Club",
    tier: "BRONZE",
    offer_text: "FREE TRIAL",
    deal_description: "7-day free gym membership trial",
    cta_text: "Start Trial",
    deal_available: true,
    image_url: "https://static.prod-images.emergentagent.com/jobs/35b12a18-38ba-4e6e-b1b6-90345e4b13b1/images/5f9f44a6f2ec0ace3123ea6d5134f0188f519c1769ef1fd83f97683d3131f82a.png",
    distance: "2.1km"
  }
];

// Dark map style for Google Maps
const darkMapStyles = [
  { elementType: "geometry", stylers: [{ color: "#212121" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry", stylers: [{ color: "#757575" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#2c2c2c" }] },
  { featureType: "road.arterial", elementType: "geometry", stylers: [{ color: "#373737" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#3c3c3c" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#000000" }] }
];

const cleanMapOptions = {
  styles: darkMapStyles,
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

// Static Melbourne CBD location
const STATIC_LOCATION = { lat: -37.8136, lng: 144.9631 };

// Static Ad Tile Component
const StaticAdTile = ({ ad, onDealClick }) => {
  const [showDealModal, setShowDealModal] = useState(false);

  const getTierBadgeColor = () => {
    switch (ad.tier?.toUpperCase()) {
      case 'GOLD': return 'bg-yellow-500 text-black';
      case 'SILVER': return 'bg-gray-400 text-black';
      case 'BRONZE': return 'bg-amber-700 text-white';
      default: return 'bg-gray-600 text-white';
    }
  };

  const handleClick = () => {
    if (ad.deal_available) {
      setShowDealModal(true);
    }
  };

  const handleSaveDeal = () => {
    toast.success(`Deal saved: ${ad.business_name}`, {
      description: ad.deal_description
    });
    setShowDealModal(false);
    if (onDealClick) onDealClick(ad);
  };

  return (
    <>
      <div 
        className="relative overflow-hidden rounded-2xl bg-gray-900 shadow-2xl cursor-pointer transition-transform hover:scale-[1.02] active:scale-[0.98]"
        style={{ height: '100%' }}
        onClick={handleClick}
        data-testid={`ad-tile-${ad.id}`}
      >
        <img 
          src={ad.image_url} 
          alt={ad.business_name}
          className="w-full h-full object-cover rounded-2xl"
          onError={(e) => { e.target.src = BOOMA_LOGO_INVERTED; }}
        />
        
        {/* Tier Badge */}
        <div className={`absolute top-3 left-3 px-3 py-1 rounded text-xs font-bold ${getTierBadgeColor()}`}>
          {ad.tier}
        </div>
        
        {/* Deal Badge */}
        {ad.deal_available && (
          <div className="absolute top-3 right-3 bg-amber-500 text-black px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1">
            <span>🎁</span> DEAL
          </div>
        )}

        {/* Business Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 rounded-b-2xl">
          <p className="text-white font-bold text-lg truncate">{ad.business_name}</p>
          {ad.offer_text && (
            <p className="text-amber-400 text-sm font-semibold truncate">{ad.offer_text}</p>
          )}
          {ad.distance && (
            <p className="text-green-400 text-sm mt-1">{ad.distance} away</p>
          )}
        </div>
      </div>

      {/* Deal Modal */}
      {showDealModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-4"
          onClick={() => setShowDealModal(false)}
        >
          <div 
            className="bg-gray-900 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Deal Image */}
            <div className="h-48 relative">
              <img 
                src={ad.image_url} 
                alt={ad.business_name}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
              <button 
                onClick={() => setShowDealModal(false)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 rounded-full flex items-center justify-center text-white text-xl hover:bg-black/70 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Deal Content */}
            <div className="p-6">
              <h3 className="text-2xl font-bold text-white mb-2">{ad.business_name}</h3>
              
              <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-4 mb-4">
                <p className="text-amber-400 font-semibold text-xl">{ad.offer_text}</p>
                <p className="text-gray-300 text-sm mt-1">{ad.deal_description}</p>
              </div>

              <p className="text-gray-400 text-base mb-4">
                📍 {ad.distance} away
              </p>

              <div className="bg-gray-800 rounded-lg p-4 mb-4">
                <p className="text-gray-400 text-sm mb-1">How it works:</p>
                <p className="text-gray-300 text-base">
                  Save this deal to your Booma app. Show the code at {ad.business_name} to redeem.
                </p>
                <p className="text-amber-400 text-sm mt-2">⏱️ Expires 24 hours after saving</p>
              </div>

              <button
                onClick={handleSaveDeal}
                className="w-full bg-amber-500 hover:bg-amber-600 text-black font-bold py-4 px-6 rounded-xl text-lg transition-colors flex items-center justify-center gap-2"
                data-testid={`save-deal-${ad.id}`}
              >
                <span>💾</span>
                {ad.cta_text || "Save to My Deals"}
              </button>

              <p className="text-gray-500 text-sm text-center mt-3">
                Deal will appear in your Booma passenger app
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default function DrivePanelDemo() {
  const [time] = useState(new Date());
  const [savedDeals, setSavedDeals] = useState([]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const handleDealSaved = (ad) => {
    setSavedDeals(prev => [...prev, ad.id]);
  };

  // Spotify mock data
  const spotifyTrack = {
    title: "Daylight",
    artist: "David Kushner",
    progress: 45
  };

  if (loadError) {
    return (
      <div className="h-screen w-screen bg-gray-950 flex items-center justify-center">
        <div className="text-red-500">Error loading Google Maps</div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-screen w-screen bg-gray-950 flex items-center justify-center">
        <div className="text-white animate-pulse">Loading maps...</div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen bg-gray-950 flex items-center justify-center overflow-hidden" data-testid="drive-panel-demo">
      {/* Fixed Tablet Frame */}
      <div 
        className="relative bg-black rounded-lg overflow-hidden shadow-2xl flex flex-col"
        style={{ 
          width: `${TABLET_WIDTH}px`, 
          height: `${TABLET_HEIGHT}px`,
          maxWidth: '100vw',
          maxHeight: '100vh'
        }}
      >
        {/* Demo Mode Banner - fixed height */}
        <div className="flex-shrink-0 bg-blue-600 text-white text-center py-2 text-sm font-medium z-50">
          📺 Demo Mode - Static Ads Display (Click any ad to view deal)
        </div>

        {/* Main Content Area - fills remaining space */}
        <div className="flex-1 relative p-4 overflow-hidden">
          {/* Saved Deals Counter */}
          {savedDeals.length > 0 && (
            <div className="absolute top-2 right-4 z-50 bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold">
              ✓ {savedDeals.length} deal{savedDeals.length > 1 ? 's' : ''} saved
            </div>
          )}

          {/* 2x2 Grid of Ad Tiles */}
          <div className="h-full grid grid-cols-2 grid-rows-2 gap-4 mx-4">
            {DEMO_ADS.map((ad) => (
              <div key={ad.id} className="min-h-0">
                <StaticAdTile 
                  ad={ad}
                  onDealClick={handleDealSaved}
                />
              </div>
            ))}
          </div>

          {/* Centered Map Overlay - Static Google Maps */}
          <div 
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-40 pointer-events-none"
            style={{ 
              width: '140px', 
              height: '280px',
              filter: 'drop-shadow(0 25px 50px rgba(0,0,0,0.9))'
            }}
          >
            {/* Phone Frame */}
            <div className="w-full h-full bg-black rounded-[28px] p-1.5 border-2 border-gray-800 shadow-inner relative">
              {/* Notch */}
              <div className="absolute top-2 left-1/2 -translate-x-1/2 w-12 h-1 bg-gray-900 rounded-full" style={{ zIndex: 9999 }}></div>
              
              {/* Static Map */}
              <div className="relative w-full h-full rounded-[22px] overflow-hidden bg-gray-950 pointer-events-auto">
                <GoogleMap
                  mapContainerStyle={{ width: "100%", height: "100%" }}
                  center={STATIC_LOCATION}
                  zoom={14}
                  options={cleanMapOptions}
                />
                
                {/* Static Trip Info Overlay */}
                <div className="absolute top-8 left-2 right-2 bg-black/70 backdrop-blur-sm rounded-lg px-2 py-1">
                  <p className="text-white text-[10px] font-medium truncate">Melbourne CBD</p>
                  <p className="text-green-400 text-[8px]">Demo Mode</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Spotify Player Bar with Time - fixed at bottom */}
        <div className="flex-shrink-0 h-16 bg-gray-900/95 backdrop-blur-sm flex items-center px-4 border-t border-gray-800">
          {/* Time Display */}
          <div className="flex items-center gap-2 mr-4 pr-4 border-r border-gray-700">
            <span className="text-white font-mono text-xl tabular-nums">
              {time.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>

          {/* Spotify Logo */}
          <div className="w-10 h-10 rounded-full bg-[#1DB954] flex items-center justify-center mr-3 flex-shrink-0">
            <svg className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
          </div>

          {/* Track Info */}
          <div className="flex-1 min-w-0">
            <p className="text-white font-medium text-sm truncate">{spotifyTrack.title}</p>
            <p className="text-gray-400 text-xs truncate">{spotifyTrack.artist}</p>
          </div>

          {/* Progress Bar */}
          <div className="w-32 mx-4">
            <div className="h-1 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full bg-[#1DB954] rounded-full"
                style={{ width: `${spotifyTrack.progress}%` }}
              />
            </div>
            <p className="text-gray-500 text-[10px] text-center mt-0.5">1:56</p>
          </div>

          {/* Playback Controls */}
          <div className="flex items-center gap-2">
            <button className="text-gray-400 hover:text-white transition-colors p-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z"/>
              </svg>
            </button>
            <button className="w-10 h-10 rounded-full bg-white flex items-center justify-center hover:scale-105 transition-transform">
              <svg className="w-4 h-4 text-black" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            </button>
            <button className="text-gray-400 hover:text-white transition-colors p-1">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z"/>
              </svg>
            </button>
          </div>

          {/* Volume */}
          <button className="text-gray-400 hover:text-white transition-colors p-1 ml-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
